import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { Link } from 'react-router-dom'
import { Button, ButtonVariant } from '../../common/Button'
import { TextInput } from '../../common/TextInput'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useFormErrorMessage } from '../../../hooks/useFormErrorMessage'
import { theme } from '../../../theme/theme'
import { ToastType } from '../../common/Toast/toast-type'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { FormControl } from '../../common/FormControl'
import { retrievePasswordSchema } from '../../../forms/schemas/retrieve_password'

interface PasswordFormProps {
  setPassword: (password: string) => void
  isSettingPassword: boolean
  // "Password" for a new account, "New password" for an old one -- a brand new
  // account has no old password for this one to replace.
  passwordLabel: string
  submitLabel: string
  // Where "send me a new link" goes. The two pages that ask for a password are
  // reached by two different mails, and each has to send people back to the one
  // that fits their case.
  sendNewLinkUrl: string
}

// The password field, shared by the pages a mailed link opens: choosing a
// password for a brand new account and choosing a new one for an old account.
// Both post the same token to the same endpoint -- only the words around them
// differ -- so the field and the rules live here once. The password is asked
// for once, without a confirmation field, to match the app.
export const PasswordForm = ({
  setPassword,
  isSettingPassword,
  passwordLabel,
  submitLabel,
  sendNewLinkUrl,
}: PasswordFormProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()
  const { setToastNotification } = useToastNotifications()

  const getDefaultValues = (): InferType<typeof retrievePasswordSchema> => {
    return {
      password: '',
    }
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(retrievePasswordSchema),
    defaultValues: getDefaultValues(),
  })

  const onSubmit = async (
    data: InferType<typeof retrievePasswordSchema>
  ): Promise<void> => {
    const { password } = data
    setPassword(password)
  }

  const onError = (): void => {
    if (!isValid) return
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const loading = isSettingPassword

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="flex flex-col gap-4"
    >
      <Controller
        control={control}
        name="password"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            error={errors.password && getErrorMessage(errors.password)}
            name={name}
            label={passwordLabel}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              type="password"
              error={!!errors.password}
            />
          </FormControl>
        )}
      />
      <div className="mt-4 flex flex-col gap-4 md:mt-12">
        <Button
          type="submit"
          style={{
            ...theme.textVariants.paragraph,
          }}
          className="flex h-14 w-full items-center justify-center"
          label={submitLabel}
          loading={loading}
          disabled={!isValid || loading}
          variant={ButtonVariant.PRIMARY}
        />
      </div>
      <div className="mt-2 mb-4 flex flex-col justify-center gap-1 md:mb-0 lg:flex-row">
        <p>
          {translate('authenticationRetrievePasswordView.problemsWithLink')}
        </p>
        <Link className="underline hover:text-blue" to={sendNewLinkUrl}>
          {translate('authenticationRetrievePasswordView.sendNewLink')}
        </Link>
      </div>
    </form>
  )
}
